import React, {useState, useEffect, useRef, useMemo} from 'react';
import ProtectedRoute from '../../routes/protected';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { Avatar } from 'primereact/avatar';
import NameInitials from '../../helpers/nameInitials';
import { Toast } from 'primereact/toast';
import useUserData from '../../context/user';
import careerService from '../../services/data/career.service';
import UsePerms from '../../context/userAccess';
import SkeletonSummary from './skeletonSummary';
import CareerHeader from './careerHeader';
import CareerMenu from './careerMenu';
import useCompanyData from '../../context/company';
import bootstrap from 'bootstrap/dist/js/bootstrap.bundle';
import FormGiveFeedback from '../../components/performance/forms/formGiveFeedback';

function calcTimeAgo(dateStr) {
    if (!dateStr) return null;
    const start = new Date(dateStr);
    const now = new Date();
    const diffMs = now - start;
    if (diffMs < 0) return null;
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const months = Math.floor(days / 30.44);
    const years = Math.floor(months / 12);
    const remMonths = months % 12;
    if (years > 0 && remMonths > 0) return `${years} ano${years > 1 ? 's' : ''} e ${remMonths} ${remMonths > 1 ? 'meses' : 'mês'}`;
    if (years > 0) return `${years} ano${years > 1 ? 's' : ''}`;
    if (months > 0) return `${months} ${months > 1 ? 'meses' : 'mês'}`;
    return `${days} dia${days !== 1 ? 's' : ''}`;
}

function formatDateBR(dateStr) {
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
}


export default function CareerSummary(props) {

    const [pageLoad, setPageLoad] = useState(true)
    const {user} = useUserData()
    const toast = useRef(null);
    const [userEmployee, setUserEmployee] = useState('')
    const VerifyPerms = UsePerms()
    const [showPerformanceTabs, setShowPerformanceTabs] = useState(false)
    const [isAnyManager, setIsAnyManager] = useState(false)
    const {rootUser} = useCompanyData()
    const offCanvasFeedbackRef = useRef()
    const offCanvasDatesRef = useRef()
    const [feedbackSent, setFeedbackSent] = useState(false)
    const [datesForm, setDatesForm] = useState({ hire_date: '', position_start_date: '' })
    const [datesSubmitting, setDatesSubmitting] = useState(false)

    const isOtherPerson = props.id && userEmployee && user && userEmployee.user.id !== user.uid
    const isOwnProfile = userEmployee && user && userEmployee.user?.id === user.uid
    const canEditDates = userEmployee && user && (
        isOwnProfile ||
        (userEmployee.manager && userEmployee.manager.uid === user.uid) ||
        rootUser
    )
    const missingPositionStart = canEditDates && userEmployee && !userEmployee.position_start_date

    function openFeedbackCanvas() {
        const canvas = new bootstrap.Offcanvas(offCanvasFeedbackRef.current)
        canvas.show()
    }

    function closeFeedbackCanvas() {
        const instance = bootstrap.Offcanvas.getInstance(offCanvasFeedbackRef.current)
        instance?.hide()
    }

    function openDatesCanvas() {
        setDatesForm({
            hire_date: userEmployee.hire_date || '',
            position_start_date: userEmployee.position_start_date || '',
        })
        new bootstrap.Offcanvas(offCanvasDatesRef.current).show()
    }

    function closeDatesCanvas() {
        bootstrap.Offcanvas.getInstance(offCanvasDatesRef.current)?.hide()
    }

    async function handleSaveDates(e) {
        e.preventDefault()
        setDatesSubmitting(true)
        try {
            const updated = await careerService.updateCareerDates({
                company_id: localStorage.getItem('companyId'),
                employee_id: userEmployee.id,
                hire_date: datesForm.hire_date || '',
                position_start_date: datesForm.position_start_date || '',
            })
            setUserEmployee(prev => ({ ...prev, ...updated }))
            closeDatesCanvas()
            toast.current.show({ severity: 'success', summary: 'Datas salvas!', life: 3000 })
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.response?.data?.[0] || 'Erro ao salvar datas'
            toast.current.show({ severity: 'error', summary: 'Erro', detail: msg, life: 4000 })
        } finally {
            setDatesSubmitting(false)
        }
    }

    function onFeedbackSuccess() {
        setFeedbackSent(true)
        closeFeedbackCanvas()
        toast.current.show({ severity: 'success', summary: '🎉 Feedback enviado!', detail: `Seu feedback para ${userEmployee?.user?.name} foi enviado com sucesso.`, life: 4000 })
    }

    useEffect(() => {

        getData()
        
    }, [])

    function getData() {

        const companyId = localStorage.getItem("companyId")
        let data = { company_id: companyId }

        if (props.id) {
            data.id = props.id
            // Busca o employee do usuário logado para saber se é gestor de qualquer equipe
            careerService.getEmployeeSummary({ company_id: companyId }).then((me) => {
                if ((me.team && me.team.length > 0) || rootUser) setIsAnyManager(true)
            }).catch((error) => {
                console.log(error.response)
            })
        }

        careerService.getEmployeeSummary(data).then((response) => {    
            setUserEmployee(response)
        }).catch((error) => {
            console.log(error.response)
        }).finally(() => {
            setPageLoad(false)
        })
    
    }

    useMemo(() => {
        // if user is superior, show performance tabs
        if (userEmployee && userEmployee.manager && userEmployee.manager.uid === user.uid) {
            setShowPerformanceTabs(true)
        }
        // if its the user itself, show performance tabs
        if (userEmployee && userEmployee.user.id === user.uid) {
            setShowPerformanceTabs(true)
        }
        // if user has perms to create eprformance review, show performance tabs
        if (VerifyPerms(['add_performancereview']) || rootUser) {
            setShowPerformanceTabs(true)
        }
    }, [userEmployee, user])

    return (
        <ProtectedRoute>
            
            <Toast ref={toast} position="top-right" />

            <div className="container-fluid">

                <CareerHeader Employee={userEmployee} />
                
                <div className='my-4 mx-5 py-3 rounded-top' style={{minHeight: '500px'}}>

                    <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 mb-0'>
                        <CareerMenu EmployeeId={userEmployee.id} showPerformanceTabs={showPerformanceTabs} showTransferTab={showPerformanceTabs || isAnyManager} Page='summary' />
                        {
                            isOtherPerson &&
                            <button
                                className='btn btn-success btn-sm me-1 mb-1'
                                onClick={openFeedbackCanvas}
                            >
                                <FontAwesomeIcon icon={solid('star')} className='me-2' />
                                {feedbackSent ? 'Dar outro Feedback' : 'Dar Feedback'}
                            </button>
                        }
                    </div>

                    <div className='bg-white py-4 rounded'>

                        {
                            pageLoad ?
                            <SkeletonSummary />
                            :
                            <>

                            {missingPositionStart && (
                                <div className='mx-3 mb-3 alert alert-warning alert-dismissible d-flex align-items-center gap-3 py-2' role='alert'>
                                    <FontAwesomeIcon icon={solid('circle-exclamation')} className='flex-shrink-0' />
                                    <div className='flex-grow-1 small'>
                                        <strong>Informação pendente:</strong> A data de início no cargo atual ainda não foi preenchida.
                                    </div>
                                    <button className='btn btn-warning btn-sm btn-rounded text-nowrap' onClick={openDatesCanvas}>
                                        <FontAwesomeIcon icon={solid('pen')} className='me-1' />
                                        Preencher
                                    </button>
                                    <button type='button' className='btn-close' data-bs-dismiss='alert' aria-label='Close' />
                                </div>
                            )}

                            <div className='row'>

                                <div className='col'>
                                    <div className='p-2 m-1'>
                                        <table className='table font1em table-borderless'>
                                            <thead>
                                                <tr className='border-bottom'>
                                                    <th className='font110' colSpan={2}>
                                                        Seu Cargo Atual
                                                        {
                                                            VerifyPerms(['change_jobposition']) && userEmployee ?
                                                            <a href={`/settings/users-management/${userEmployee.id}/edit/`} className='btn btn-link border btn-sm btn-rounded text-green ms-2'>
                                                                <FontAwesomeIcon className='font095' icon={solid('edit')} />
                                                            </a>
                                                            : null
                                                        }
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td className='fw-semibold'>Matrícula</td>
                                                    <td>{ userEmployee.employeeId ? userEmployee.employeeId : "-"}</td>
                                                </tr>
                                                <tr>
                                                    <td className='fw-semibold'>Organização</td>
                                                    <td>
                                                    { userEmployee.jobposition ? userEmployee.jobposition.subcompany.title : "-"}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className='fw-semibold'>Cargo</td>
                                                    <td>
                                                    { userEmployee.jobposition ? userEmployee.jobposition.title : "-"}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className='fw-semibold'>Departamento</td>
                                                    <td>
                                                    { userEmployee.jobposition ? userEmployee.jobposition.department : "-"}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className='fw-semibold'>Centro de Custo</td>
                                                    <td>
                                                    { userEmployee.jobposition ? userEmployee.jobposition.costcenter : "-"}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className='fw-semibold'>Localidade</td>
                                                    <td>
                                                    { 
                                                        userEmployee.jobposition ? 
                                                        <>
                                                            <FontAwesomeIcon className='font095' icon={solid('location-dot')} /> {userEmployee.jobposition.subcompany.address}
                                                        </>
                                                        
                                                        : "-"
                                                    }
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className='fw-semibold'>Data Início no Cargo</td>
                                                    <td>
                                                        {userEmployee.position_start_date
                                                            ? formatDateBR(userEmployee.position_start_date)
                                                            : <span className='text-muted fst-italic small'>não informado</span>
                                                        }
                                                        {canEditDates && (
                                                            <button className='btn btn-link btn-sm p-0 ms-2 text-green' onClick={openDatesCanvas} title='Editar datas'>
                                                                <FontAwesomeIcon icon={solid('pen')} className='font085' />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className='fw-semibold'>Tempo de Empresa</td>
                                                    <td>
                                                        {userEmployee.hire_date
                                                            ? calcTimeAgo(userEmployee.hire_date)
                                                            : <span className='text-muted fst-italic small'>não informado</span>
                                                        }
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className='fw-semibold'>Tempo no Cargo</td>
                                                    <td>
                                                        {userEmployee.position_start_date
                                                            ? calcTimeAgo(userEmployee.position_start_date)
                                                            : <span className='text-muted fst-italic small'>não informado</span>
                                                        }
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className='col'>
                                    <div className='p-2 m-1'>
                                        <table className='table font1em table-borderless'>
                                            <thead>
                                                <tr className='border-bottom'>
                                                    <th className='font110' colSpan={2}>
                                                        Infos de Contato (público)
                                                        {
                                                            userEmployee ?
                                                            <>
                                                            {
                                                                user.uid === userEmployee.user.id ?
                                                                <a href='/settings/edit-user/' className='btn btn-link border btn-sm btn-rounded text-green ms-2'>
                                                                    <FontAwesomeIcon className='font095' icon={solid('edit')} />
                                                                </a>
                                                                : null
                                                            }
                                                            </>
                                                            : null
                                                        }
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td className='fw-semibold'>Telefone</td>
                                                    <td>
                                                    {
                                                        userEmployee ?
                                                        <>
                                                        <div>
                                                            {userEmployee.user.phone ? userEmployee.user.phone : "-"}
                                                        </div>
                                                        {
                                                            userEmployee.user.phone2 ? 
                                                            <div>{userEmployee.user.phone2}</div>
                                                            : 
                                                            null
                                                        }
                                                        </>
                                                        :
                                                        null
                                                    }
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className='fw-semibold'>Email</td>
                                                    <td>
                                                    {
                                                        userEmployee ?
                                                        <>
                                                        <div>
                                                            {userEmployee.user.email ? userEmployee.user.email : "-"}
                                                        </div>
                                                        {
                                                            userEmployee.user.email2 ? 
                                                            <div>{userEmployee.user.email2}</div>
                                                            : 
                                                            null
                                                        }
                                                        </>
                                                        :
                                                        null
                                                    }
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    {
                                        userEmployee && userEmployee.manager ?
                                        <>
                                        <div className='p-2 m-1'>
                                            <table className='table font1em table-borderless'>
                                                <thead>
                                                    <tr className='border-bottom'>
                                                        <th className='font110' colSpan={2}>Gestor Imediato</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td width={20}>
                                                        {
                                                            userEmployee.manager.photo ?
                                                            <Avatar image={userEmployee.manager.photo} style={{ backgroundColor: '#8463a9', color: '#ffffff', fontWeight: 'bold' }} shape='circle' />
                                                            :
                                                            <Avatar label={NameInitials( userEmployee.manager.name )} style={{ backgroundColor: '#8463a9', color: '#ffffff', fontWeight: 'bold' }} shape='circle' />
                                                        }
                                                        </td>
                                                        <td>
                                                            <div className='text-green font110 fw-bold' onClick={()=>window.open(`/career/summary/${userEmployee.manager.id}/`,'_self', 'rel=noopener noreferrer')} role='button'>
                                                                {userEmployee.manager.name}
                                                            </div>
                                                            {
                                                                userEmployee.manager.email?
                                                                <div>
                                                                <FontAwesomeIcon className='font095 me-1' icon={solid('envelope')} /> {userEmployee.manager.email}
                                                                </div>
                                                                :
                                                                null
                                                            }
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        </>
                                        : null
                                    }
                                </div>
                                {
                                    userEmployee && userEmployee.team.length > 0 ?
                                    <>
                                    <div className='col'>
                                        <div className='p-2 rounded border m-1'>
                                            <table className='table font1em px-1'>
                                                <thead>
                                                    <tr className='border-bottom'>
                                                        <th className='font110' colSpan={2}>
                                                            {
                                                                userEmployee ?
                                                                <>
                                                                {
                                                                    user.uid === userEmployee.user.id ?
                                                                    <>
                                                                    Minha Equipe
                                                                    </>
                                                                    :
                                                                    <>
                                                                    Equipe
                                                                    </>
                                                                }
                                                                </>
                                                                : null
                                                            }
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                {
                                                    userEmployee.team.map((item, index) =>
                                                    <tr key={index}>
                                                        <td width={20} className='border-bottom-0'>
                                                        {
                                                            item.photo ?
                                                            <Avatar image={item.photo} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' />
                                                            :
                                                            <Avatar label={NameInitials( item.name )} style={{ backgroundColor: '#57c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' />
                                                        }
                                                        </td>
                                                        <td className='border-bottom-0'>
                                                            <div className='text-green fw-bold'>
                                                                <span onClick={()=>window.open(`/career/summary/${item.id}/`,'_self', 'rel=noopener noreferrer')} role='button' >
                                                                    {item.name}
                                                                </span>
                                                            </div>
                                                            <div>{item.jobposition.title}</div>
                                                        </td>
                                                    </tr>
                                                    )
                                                }
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    </>
                                    : null
                                }
                            </div>
                            </>
                        }
                        
                    </div>
                </div>
            </div>

            {/* Offcanvas: Dar Feedback */}
            <div className='offcanvas offcanvas-end' tabIndex='-1' ref={offCanvasFeedbackRef}>
                <div className='offcanvas-header border-bottom'>
                    <h6 className='offcanvas-title fw-bold'>
                        <FontAwesomeIcon icon={solid('star')} className='me-2 text-success' />
                        Dar Feedback para {userEmployee?.user?.name}
                    </h6>
                    <button type='button' className='btn-close' onClick={closeFeedbackCanvas} />
                </div>
                <div className='offcanvas-body'>
                    {userEmployee && (
                        <FormGiveFeedback
                            employee={{ id: userEmployee.id }}
                            feedbackRequest={null}
                            onSuccess={onFeedbackSuccess}
                            onCancel={closeFeedbackCanvas}
                        />
                    )}
                </div>
            </div>

            {/* Offcanvas: Datas do Cargo */}
            <div className='offcanvas offcanvas-end' tabIndex='-1' ref={offCanvasDatesRef} style={{ width: 420 }}>
                <div className='offcanvas-header border-bottom'>
                    <h6 className='offcanvas-title fw-bold'>
                        <FontAwesomeIcon icon={solid('calendar-days')} className='me-2 text-green' />
                        Datas do Colaborador
                    </h6>
                    <button type='button' className='btn-close' onClick={closeDatesCanvas} />
                </div>
                <div className='offcanvas-body'>
                    <p className='text-muted small mb-4'>
                        Informe as datas para calcular o tempo de empresa e tempo no cargo atual.
                    </p>
                    <form onSubmit={handleSaveDates}>
                        <div className='mb-3'>
                            <label className='form-label small fw-semibold'>
                                Data de contratação
                                <span className='text-muted fw-normal ms-1'>(entrada na empresa)</span>
                            </label>
                            <input
                                type='date'
                                className='form-control form-control-sm'
                                value={datesForm.hire_date}
                                onChange={e => setDatesForm(f => ({ ...f, hire_date: e.target.value }))}
                            />
                        </div>
                        <div className='mb-4'>
                            <label className='form-label small fw-semibold'>
                                Data de início no cargo atual
                                <span className='text-danger ms-1'>*</span>
                            </label>
                            <input
                                type='date'
                                className='form-control form-control-sm'
                                value={datesForm.position_start_date}
                                onChange={e => setDatesForm(f => ({ ...f, position_start_date: e.target.value }))}
                                required
                            />
                            <div className='form-text'>
                                Quando o colaborador assumiu o cargo atual.
                            </div>
                        </div>
                        <div className='d-flex gap-2'>
                            <button type='submit' className='btn btn-success btn-sm btn-rounded px-3' disabled={datesSubmitting}>
                                {datesSubmitting && <span className='spinner-border spinner-border-sm me-1' />}
                                Salvar
                            </button>
                            <button type='button' className='btn btn-outline-secondary btn-sm btn-rounded' onClick={closeDatesCanvas}>
                                Cancelar
                            </button>
                        </div>
                    </form>
                </div>
            </div>

        </ProtectedRoute>
    )
}